/**
 * Maps Firebase HTTPS callable (and common Cloud Functions) errors to user-safe messages.
 * Pillar 3/8: consistent UX; technical details stay in console / Sentry.
 *
 * Works with firebase/compat errors where `error.code` is set (e.g. functions/unauthenticated).
 */

/** @param {unknown} err */
function getErrorCode(err) {
  if (err && typeof err === 'object' && 'code' in err && typeof err.code === 'string') {
    return err.code;
  }
  return null;
}

/** @param {unknown} err */
function getErrorMessageString(err) {
  if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
    return err.message.trim();
  }
  return '';
}

/**
 * Extra server context from Firebase callable errors (compat + modular shapes).
 * @param {unknown} err
 * @returns {string}
 */
export function getFirebaseCallableErrorDetail(err) {
  if (!err || typeof err !== 'object') return '';
  const parts = [];
  const { details, customData } = err;
  if (typeof details === 'string' && details.trim()) {
    parts.push(details.trim());
  } else if (details && typeof details === 'object') {
    try {
      const s = JSON.stringify(details);
      if (s && s !== '{}') parts.push(s.slice(0, 500));
    } catch (_) {
      /* ignore */
    }
  }
  if (customData && typeof customData === 'object') {
    try {
      const s = JSON.stringify(customData);
      if (s && s !== '{}') parts.push(s.slice(0, 500));
    } catch (_) {
      /* ignore */
    }
  }
  return parts.join(' · ').slice(0, 600);
}

/**
 * @param {unknown} error - Firebase HttpsError or generic Error
 * @returns {{ userMessage: string, code: string | null }}
 */
export function getFirebaseHttpsCallableUserMessage(error) {
  const code = getErrorCode(error);
  const generic =
    'Something went wrong on our end. Please try again in a moment. If it keeps happening, contact support.';
  const msg = getErrorMessageString(error);

  if (!code) {
    if (/failed to fetch|networkerror|load failed|network request failed/i.test(msg)) {
      return {
        userMessage:
          'Could not reach the server (network). Check your connection. On localhost, confirm Cloud Functions are deployed for this Firebase project or the Functions emulator is running.',
        code: null,
      };
    }
    if (msg && !/^functions\//i.test(msg)) {
      return { userMessage: `${generic} (${msg})`, code: null };
    }
    return { userMessage: generic, code: null };
  }

  const map = {
    'functions/unauthenticated': 'Your session expired or you are not signed in. Please sign in and try again.',
    'functions/permission-denied': 'You do not have permission to do this.',
    'functions/not-found': 'This action is not available right now. Please try again later.',
    'functions/invalid-argument': 'Some information was not accepted. Check your input and try again.',
    'functions/failed-precondition': 'This action cannot be completed in the current state. Try again or refresh the page.',
    'functions/out-of-range': 'The request was out of allowed limits. Adjust your input and try again.',
    'functions/unimplemented': 'This feature is not available yet.',
    'functions/internal': generic,
    'functions/unavailable': 'Our servers are busy. Please try again in a few minutes.',
    'functions/data-loss': generic,
    'functions/deadline-exceeded': 'The request took too long and timed out. Try again with a shorter input or later.',
    'functions/cancelled': 'The request was cancelled.',
    'functions/already-exists': 'This item already exists.',
    'functions/resource-exhausted': 'Too many requests. Please wait a moment and try again.',
    'functions/aborted': 'The operation was interrupted. Please try again.',
  };

  const userMessage = map[code] || generic;
  return { userMessage, code };
}

/**
 * Wraps a callable error as a new Error with a safe .message for UI; preserves original on .cause when available.
 * @param {unknown} error
 * @param {{ feature?: string }} [context]
 */
export function wrapFirebaseCallableErrorForUser(error, context = {}) {
  const { userMessage, code } = getFirebaseHttpsCallableUserMessage(error);
  const wrapped = new Error(userMessage);
  wrapped.name = 'FirebaseCallableUserError';
  if (code) {
    wrapped.code = code;
  }
  if (context.feature) {
    wrapped.feature = context.feature;
  }
  try {
    wrapped.cause = error;
  } catch (e) {
    console.warn('[firebaseHttpsCallableUserMessage] Could not set error.cause', e);
  }
  return wrapped;
}
